import * as v from 'valibot';
import type { LoanSummary, SummaryByLoanType } from 'tio-common/types/loan-summary';
import type { AnswerProfileExtension as AnswerProfileModel } from 'tio-data/data/resources/answer-profile.ext';
import type { GeneratePaymentPlanPayload } from 'tio-data/requests/payment-plan-explorer';

export const REQUESTING_APP = 'SF-0726';

// The pilot no longer asks for an income growth rate; assume a flat 3%.
export const DEFAULT_AGI_GROWTH_RATE = 0.03;

export type SortBy = 'first_month_then_total' | 'total_then_first_month';

/**
 * One request body per loan type, matching tio-user-profile's SortPlansRequest
 * pydantic model field for field. All money values are in cents and interest
 * rates are decimal fractions, the same units the loan summary already uses.
 */
export type SortPlansPayload = {
  interest_rate_annual: number;
  current_balance: number;
  current_paid_months: number;
  current_plan: string | null;
  original_disbursement_amount: number | null;
  agi: number;
  agi_growth_rate: number;
  number_of_children: number;
  number_of_adults: number;
  first_loan_time: string;
  last_loan_time: string;
  loan_type: string;
  tlf_amounts: number[];
  pslf: boolean;
  // When true, sort-plans emits both with- and without-PSLF rows for the IDR
  // plans (so the best plan can use PSLF and the current plan can match either).
  pslf_eligible: boolean;
  residence_state: string;
  sort_by: SortBy;
  // This loan group's share of the combined Direct + FFEL balance. The python
  // service scales the (household-level) income-driven payment and TLF by it so
  // multi-loan profiles don't count them once per group. 1.0 for single-group.
  multiplier: number;
  // The following are not SortPlansRequest fields (the python service ignores
  // them); they are persisted so the results/PDF can echo the user's inputs and
  // pick the right "current plan" row.
  monthly_payment: number; // cents, entered current payment
  payoff_goal: string;
  afford_payments: string;
  pslf_eligibility: string; // derived 'yes' | 'no' for the inputs echo
  // The TLF amount (cents) and PSLF flag the borrower is *currently* on, used to
  // match the current-plan row among the with/without variants. 0 / false unless
  // the user said they're currently pursuing TLF / PSLF.
  current_tlf_amount: number;
  current_pslf: boolean;
  // The raw loan-forgiveness answers, echoed so the inputs summary can show them
  // back exactly as the borrower entered them (ignored by the python service).
  currently_working_toward: string;
  forgiveness_program: string;
  employment_type: string;
  plan_in_gov: string;
  plan_as_teacher: string;
  tlf_qualifies_17500: boolean;
};

// Loan types that can be on income-driven plans and are TLF-eligible. The
// household income-driven payment / TLF benefit are split across these by
// balance share. PLUS only ever gets balance-based plans (no IDR) and is never
// TLF-eligible, so it is excluded from the split and stays at multiplier 1.0.
const IDR_ELIGIBLE_LOAN_TYPES = ['direct', 'ffel'];

export type SortedPlanRow = {
  plan: string;
  title: string;
  tlf_amount: number;
  pslf: boolean;
  first_month_payment: number;
  max_monthly_payment: number;
  total_payment: number;
  employee_paid: number;
  employer_paid: number;
  forgiveness_paid: number;
  // Forgiveness split out by source so the results can show TLF and PSLF in
  // separate columns. pslf_forgiveness is the balance forgiven at the PSLF
  // cutoff and is only populated for rows that actually used PSLF.
  tlf_forgiveness: number;
  pslf_forgiveness: number;
  subsidy_paid: number;
  num_payments: number;
  paid_months_used: number;
  paid_months_preserved: boolean;
  warning?: string | null;
};

export type SortPlansOutput = {
  loan_type?: string;
  plans: SortedPlanRow[];
  sort_by: string;
  available_plans_warning?: string | null;
};

export const FIRST_LOAN_TIME_VALUES = [
  'old_borrower',
  'old_ibr_cohort',
  'new_ibr_cohort',
  'obbb_cohort',
] as const;

export const LAST_LOAN_TIME_VALUES = ['pre_obbb_cohort', 'obbb_cohort'] as const;

// Forgiveness amounts in cents, matching the cents balances the payloads pass through
export const TLF_AMOUNTS_BY_OPTION: Record<string, number[]> = {
  none: [0],
  tlf5000: [0, 500_000],
  tlf17500: [0, 1_750_000],
};

export const TLF_OPTION_VALUES = Object.keys(TLF_AMOUNTS_BY_OPTION);

// Every RepaymentPlans value sort-plans can return, for translated display names
// and for the editable current-plan dropdown on each loan card.
export const PLAN_KEYS = [
  'noRepaymentPlan',
  'tenYearsStandard',
  'extendedPlan',
  'refinancing',
  'paye',
  'repaye',
  'graduated',
  'ibr',
  'icr',
  'repaymentAssistancePlan',
  'newStandardPlan',
] as const;

// Options for the editable "current plan" dropdown on each loan card. SAVE
// (repaye), "no repayment plan", and refinancing are intentionally excluded —
// they are not selectable current plans.
const EXCLUDED_CURRENT_PLANS = ['repaye', 'noRepaymentPlan', 'refinancing'];
export const CURRENT_PLAN_OPTION_KEYS = PLAN_KEYS.filter(
  (key) => !EXCLUDED_CURRENT_PLANS.includes(key)
);

// repaymentPlan values from the loan summary that are not real plans on the
// python side; sort-plans expects null for "no current plan"
const NON_PLAN_VALUES = ['', 'noRepaymentPlan', 'fivePercentUndergrad'];

// Kept in-app so this pilot stays self-contained; mirrors tio-common's
// strategy-finder consts, which is not reachable cross-package from vite
// (its exports map routes ./components/* to .gts only).
export const STATE_OPTIONS = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'Washington, DC', abbr: 'DC' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' },
  { name: 'Puerto Rico', abbr: 'PR' },
  { name: 'Guam', abbr: 'GU' },
  { name: 'American Samoa', abbr: 'AS' },
  { name: 'U.S. Virgin Islands', abbr: 'VI' },
  { name: 'Northern Mariana Islands', abbr: 'MP' },
  { name: 'Marshall Islands', abbr: 'MH' },
  { name: 'Palau', abbr: 'PW' },
  { name: 'Federated States of Micronesia', abbr: 'FM' },
] as const;

// Loan-forgiveness flow values, mirroring the existing Repayment Strategy Finder.
export const WORKING_TOWARD_VALUES = ['yes', 'no-and-not-interested', 'no-but-want-learn'] as const;
export const FORGIVENESS_PROGRAM_VALUES = [
  'PublicServiceLoanForgiveness',
  'TeacherLoanForgiveness',
  'IncomeDrivenLoanForgiveness',
] as const;
export const PLAN_IN_GOV_VALUES = ['for-10-years-more', 'not-for-10-years'] as const;
export const PLAN_AS_TEACHER_VALUES = ['for-5-years-and-more', 'less-than-5-years'] as const;

// Teacher Loan Forgiveness tier (cents): $17,500 for highly-qualified math/science/
// special-education teachers who meet the criteria, otherwise $5,000.
export function teacherTlfAmountCents(qualifies17500: boolean): number {
  return qualifies17500 ? 1_750_000 : 500_000;
}

// The combined employment-type string the existing finder stores on the answer
// profile, derived from the three employment checkboxes.
export function deriveEmploymentType(isGov: boolean, isTeacher: boolean, isOther: boolean): string {
  if (isGov && isTeacher) return 'teacher&government-tribal-nonprofit';
  if (isTeacher) return 'teacher';
  if (isGov) return 'government-tribal-nonprofit';
  if (isOther) return 'other-private-sector';
  return '';
}

// Plan keys whose rows can carry PSLF forgiveness, mirroring the python
// is_pslf_eligible_plan (RAP/PAYE/ICR/IBR). PSLF only counts on income-driven
// plans, so the engine emits a single pslf=false row for every other plan.
export const PSLF_ELIGIBLE_PLAN_KEYS = ['repaymentAssistancePlan', 'paye', 'icr', 'ibr'];

export function isPslfEligiblePlanKey(plan: string | null | undefined): boolean {
  return !!plan && PSLF_ELIGIBLE_PLAN_KEYS.includes(plan);
}

// The questionnaire answers that are NOT per-loan (one set applies to all loans).
export type Sf0726FormModel = {
  payoffGoal: string;
  affordPayments: string;
  agi: number; // cents
  numberOfAdults: number;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  residenceState: string;
  // Loan-forgiveness flow, mirroring the existing Repayment Strategy Finder.
  currentlyWorkingToward: string; // yes | no-and-not-interested | no-but-want-learn
  currentForgivenessProgram: string; // PublicServiceLoanForgiveness | TeacherLoanForgiveness | IncomeDrivenLoanForgiveness
  madePayments: number; // "About how many qualifying payments have you made?"
  isGov: boolean; // Government / Public School / Tribal Organization or Non-Profit
  isTeacher: boolean; // Teacher in a low income school
  isOther: boolean; // Other / Private Sector
  planInGov: string; // for-10-years-more | not-for-10-years
  planAsTeacher: string; // for-5-years-and-more | less-than-5-years
  employmentMonths: number; // gov/teacher "how many months …" sub-question
  tlfQualifies17500: boolean; // qualifies for the $17,500 TLF tier (vs $5,000)
};

// The non-zero TLF amount (cents) for a TLF option, or 0 for "none".
export function tlfLevelCents(tlfOption: string): number {
  return Math.max(0, ...(TLF_AMOUNTS_BY_OPTION[tlfOption] ?? [0]));
}

// Per-loan values shown on the editable loan cards. Money is in dollars and the
// rate is a whole percent here (display units); buildSortPlansPayloads converts
// them to the cents / decimal-fraction units the python service expects.
export type Sf0726LoanInput = {
  loanType: string;
  loanCount: number;
  currentBalanceDollars: number;
  interestRatePercent: number;
  monthlyPaymentDollars: number;
  repaymentPlan: string;
  originalDisbursementDollars: number;
};

export const sf0726FormSchema = v.object({
  payoffGoal: v.pipe(
    v.string('Please select a response'),
    v.picklist(['quick', 'slow'], 'Please select a response')
  ),
  affordPayments: v.pipe(
    v.string('Please select a response'),
    v.picklist(['yes', 'no'], 'Please select a response')
  ),
  agi: v.pipe(
    v.number('Income must be greater than $0'),
    v.minValue(1, 'Income must be greater than $0')
  ),
  numberOfAdults: v.pipe(
    v.number('Please select a response'),
    v.picklist([1, 2], 'Please select a response')
  ),
  numberOfChildren: v.pipe(v.number('Must be 0 or greater'), v.minValue(0, 'Must be 0 or greater')),
  firstLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...FIRST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  lastLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...LAST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  currentlyWorkingToward: v.pipe(
    v.string('Please select a response'),
    v.picklist([...WORKING_TOWARD_VALUES], 'Please select a response')
  ),
  residenceState: v.pipe(
    v.string('Please select a state'),
    v.minLength(2, 'Please select a state')
  ),
});

/**
 * The pilot has no user-facing sort selector; the sort key is derived from the
 * payoff-goal and affordability questions instead:
 * wants to pay off quickly AND can afford payments -> lowest total paid,
 * anything else -> lowest first monthly payment.
 */
export function deriveSortBy(payoffGoal: string, affordPayments: string): SortBy {
  if (payoffGoal === 'quick' && affordPayments === 'yes') {
    return 'total_then_first_month';
  }
  return 'first_month_then_total';
}

// True when the employment block is shown and a selected employment type's
// "how long…" sub-question is still unanswered. Only the radio selections are
// required; the months input and the TLF checkbox stay optional. Mirrors the
// component's showEmployment getter so a stale prefill (e.g. isGov true while
// the block is hidden) never blocks submission.
export function missingForgivenessSubQuestions(model: Sf0726FormModel): boolean {
  const showEmployment =
    model.currentlyWorkingToward === 'yes' || model.currentlyWorkingToward === 'no-but-want-learn';
  if (!showEmployment) {
    return false;
  }
  if (model.isGov && !model.planInGov) {
    return true;
  }
  if (model.isTeacher && !model.planAsTeacher) {
    return true;
  }
  return false;
}

export function normalizeCurrentPlan(repaymentPlan: string | null | undefined): string | null {
  if (!repaymentPlan || NON_PLAN_VALUES.includes(repaymentPlan)) {
    return null;
  }
  return repaymentPlan;
}

// Map a stored tlf_amounts array back to the form's TLF option key.
export function tlfOptionFromAmounts(amounts: number[] | undefined | null): string {
  if (amounts?.includes(1_750_000)) {
    return 'tlf17500';
  }
  if (amounts?.includes(500_000)) {
    return 'tlf5000';
  }
  return 'none';
}

// Seed the editable loan cards from the user's loan summary, converting the
// summary's cents/decimal units into the dollar/percent display units.
export function loanInputsFromSummary(loanSummary: LoanSummary): Sf0726LoanInput[] {
  return (loanSummary.summaryByLoanType || []).map((loan) => ({
    loanType: loan.loanType,
    loanCount: loan.loanIds?.length ?? 0,
    currentBalanceDollars: Math.round(loan.currentBalance) / 100,
    interestRatePercent: Math.round(loan.interestRate * 10000) / 100,
    monthlyPaymentDollars: Math.round(loan.monthlyPayment) / 100,
    // Pre-fill the current plan from the loan summary only when it is one of the
    // selectable options (SAVE, "no plan", and refinancing are excluded). The
    // dropdown stays editable, so the borrower can still change it. Anything not
    // in the allowed set is left blank for the user to pick.
    repaymentPlan: (CURRENT_PLAN_OPTION_KEYS as readonly string[]).includes(loan.repaymentPlan)
      ? loan.repaymentPlan
      : '',
    originalDisbursementDollars: loan.totalDisbursedAmount
      ? Math.round(loan.totalDisbursedAmount) / 100
      : 0,
  }));
}

// Maps a URL test flag to the loan-type card it injects. Param names match the
// loan-type keys directly so `?direct=true&ffel=true&plus=true` reads cleanly.
export const TEST_LOAN_TYPES = ['direct', 'ffel', 'plus'] as const;
export type TestLoanFlags = Partial<Record<(typeof TEST_LOAN_TYPES)[number], boolean>>;

// A zeroed loan-summary entry, used to render a blank, editable test card.
function blankSummaryEntry(loanType: string): SummaryByLoanType {
  return {
    loanType,
    loanIds: [],
    currentBalance: 0,
    interestRate: 0,
    monthlyPayment: 0,
    repaymentPlan: '',
    repaymentPlans: [],
    totalDisbursedAmount: 0,
    federalLoanTime: '',
  };
}

// Testing aid: append a blank, editable card for each requested loan type the
// user does not already have. Types the user already has are left untouched
// (their real values show as before). Returns the summary unchanged when there
// is nothing to add.
export function withTestLoanCards(loanSummary: LoanSummary, flags: TestLoanFlags): LoanSummary {
  const existing = new Set((loanSummary.summaryByLoanType || []).map((loan) => loan.loanType));
  const additions = TEST_LOAN_TYPES.filter(
    (loanType) => flags[loanType] && !existing.has(loanType)
  ).map(blankSummaryEntry);

  if (!additions.length) {
    return loanSummary;
  }
  return {
    ...loanSummary,
    summaryByLoanType: [...(loanSummary.summaryByLoanType || []), ...additions],
  };
}

export function buildSortPlansPayloads(
  formModel: Sf0726FormModel,
  loanInputs: Sf0726LoanInput[]
): SortPlansPayload[] {
  // Combined balance of the IDR/TLF-eligible (Direct + FFEL) groups, used to
  // split the household income-driven payment and TLF across them by share.
  const eligibleBalanceCents = loanInputs
    .filter((loan) => IDR_ELIGIBLE_LOAN_TYPES.includes(loan.loanType))
    .reduce((sum, loan) => sum + Math.round(loan.currentBalanceDollars * 100), 0);

  // Derive forgiveness state from the reworded questions (mirrors the existing
  // finder). "working" = actively pursuing forgiveness; "considering" also
  // includes "not yet, but want to learn", who are shown the employment block so
  // we can surface what they'd be eligible for without marking them as pursuing.
  const working = formModel.currentlyWorkingToward === 'yes';
  const considering = working || formModel.currentlyWorkingToward === 'no-but-want-learn';
  // "Currently pursuing" follows the program the borrower says they're working
  // toward, so a PSLF pursuer's current plan doesn't pick up TLF (and vice
  // versa) just because they happen to be eligible for both.
  const pursuingTlf = working && formModel.currentForgivenessProgram === 'TeacherLoanForgiveness';
  const pursuingPslf =
    working && formModel.currentForgivenessProgram === 'PublicServiceLoanForgiveness';
  // PSLF: a public-service employer planning to stay at least 10 years (PSLF needs
  // 120 qualifying payments) makes the income-driven plans PSLF-eligible.
  const govEligible = considering && formModel.isGov && formModel.planInGov === 'for-10-years-more';
  // TLF: teacher in a low-income school planning five consecutive years; the
  // amount ($17,500 vs $5,000) comes from the highly-qualified-teacher question.
  const teacherEligible =
    considering && formModel.isTeacher && formModel.planAsTeacher === 'for-5-years-and-more';
  const teacherAmount = teacherTlfAmountCents(formModel.tlfQualifies17500);
  // Months already paid: prefer the employment "how many months" answer when that
  // sub-question was shown and filled, otherwise the qualifying-payments number.
  const employmentMonthsShown = considering && (formModel.isGov || formModel.isTeacher);
  const paidMonths =
    employmentMonthsShown && formModel.employmentMonths
      ? formModel.employmentMonths
      : formModel.madePayments;

  return loanInputs.map((loan) => {
    const currentBalanceCents = Math.round(loan.currentBalanceDollars * 100);
    const eligible = IDR_ELIGIBLE_LOAN_TYPES.includes(loan.loanType);
    const multiplier =
      eligible && eligibleBalanceCents > 0 ? currentBalanceCents / eligibleBalanceCents : 1;
    // TLF and PSLF only apply to Direct/FFEL IDR plans, never PLUS.
    const tlfActive = eligible && teacherEligible;
    const tlfAmounts = tlfActive ? [0, teacherAmount] : [0];
    const pslfEligible = eligible && govEligible;
    // Which variant the borrower is currently on (for the current-plan match):
    // they must be eligible AND say they're pursuing that specific program.
    const currentTlfAmount = tlfActive && pursuingTlf ? teacherAmount : 0;
    const currentPslf = pslfEligible && pursuingPslf;

    return {
      interest_rate_annual: loan.interestRatePercent / 100,
      current_balance: currentBalanceCents,
      current_paid_months: paidMonths,
      current_plan: normalizeCurrentPlan(loan.repaymentPlan),
      original_disbursement_amount: loan.originalDisbursementDollars
        ? Math.round(loan.originalDisbursementDollars * 100)
        : null,
      agi: formModel.agi,
      agi_growth_rate: DEFAULT_AGI_GROWTH_RATE,
      number_of_children: formModel.numberOfChildren,
      number_of_adults: formModel.numberOfAdults,
      first_loan_time: formModel.firstLoanTime,
      last_loan_time: formModel.lastLoanTime,
      loan_type: loan.loanType,
      tlf_amounts: tlfAmounts,
      pslf: false,
      pslf_eligible: pslfEligible,
      residence_state: formModel.residenceState,
      sort_by: deriveSortBy(formModel.payoffGoal, formModel.affordPayments),
      multiplier,
      monthly_payment: Math.round(loan.monthlyPaymentDollars * 100),
      payoff_goal: formModel.payoffGoal,
      afford_payments: formModel.affordPayments,
      // Echoed for the inputs summary's PSLF label ('yes' / 'no').
      pslf_eligibility: pslfEligible ? 'yes' : 'no',
      current_tlf_amount: currentTlfAmount,
      current_pslf: currentPslf,
      // Raw forgiveness answers echoed back for the inputs summary.
      currently_working_toward: formModel.currentlyWorkingToward,
      forgiveness_program: formModel.currentForgivenessProgram,
      employment_type: deriveEmploymentType(
        formModel.isGov,
        formModel.isTeacher,
        formModel.isOther
      ),
      plan_in_gov: formModel.planInGov,
      plan_as_teacher: formModel.planAsTeacher,
      tlf_qualifies_17500: formModel.tlfQualifies17500,
    };
  });
}

// A parsed view of a saved recommendation: one entry per loan type, pairing the
// stored sort-plans input with its output. Shared by the comparison summary,
// results tables, and inputs summary.
export type Sf0726ResultSection = {
  loanType: string;
  input: SortPlansPayload | null;
  plans: SortedPlanRow[];
  currentPlan: string | null;
  // The variant the borrower is currently on, used to pick the "current" row.
  currentTlfAmount: number;
  currentPslf: boolean;
  enteredMonthlyPaymentCents: number;
  sortBy: string;
  availablePlansWarning: boolean;
};

export function parseResultSections(
  calculationInput: Record<string, unknown> | undefined,
  calculationOutput: Record<string, unknown> | undefined
): Sf0726ResultSection[] {
  const outputs = calculationOutput?.['data'];
  if (!Array.isArray(outputs)) {
    return [];
  }
  const inputs = calculationInput?.['data'];
  const inputList = (Array.isArray(inputs) ? inputs : []) as SortPlansPayload[];

  return (outputs as SortPlansOutput[]).map((output, index) => {
    const input = inputList[index] ?? null;
    const section: Sf0726ResultSection = {
      loanType: output.loan_type ?? input?.loan_type ?? '',
      input,
      plans: output.plans ?? [],
      currentPlan: input?.current_plan ?? null,
      currentTlfAmount: input?.current_tlf_amount ?? 0,
      currentPslf: input?.current_pslf ?? false,
      enteredMonthlyPaymentCents: input?.monthly_payment ?? 0,
      sortBy: output.sort_by,
      availablePlansWarning: !!output.available_plans_warning,
    };
    // Hide the redundant without-PSLF income-driven rows from the suggestions
    // (keeping the borrower's own current plan); see suggestedPlans.
    section.plans = suggestedPlans(section.plans, section);
    return section;
  });
}

/**
 * When the borrower is PSLF-eligible the engine returns both a with- and a
 * without-PSLF row for each income-driven plan. For an eligible borrower PSLF is
 * the recommended path, so we never *suggest* the without-PSLF variant — drop
 * it. The one exception is the borrower's own current plan, which may
 * legitimately be an IDR plan without PSLF (eligible but not pursuing); that row
 * is kept so it still shows (and highlights) as the current plan.
 *
 * When the borrower is not PSLF-eligible no with-PSLF sibling exists, so nothing
 * is dropped.
 */
export function suggestedPlans(
  plans: SortedPlanRow[],
  section: Sf0726ResultSection
): SortedPlanRow[] {
  return plans.filter((row) => {
    const hasPslfSibling = plans.some(
      (p) => p.plan === row.plan && p.tlf_amount === row.tlf_amount && p.pslf
    );
    const redundantNonPslfIdr = isPslfEligiblePlanKey(row.plan) && !row.pslf && hasPslfSibling;
    return !redundantNonPslfIdr || isCurrentPlanRow(row, section);
  });
}

// Does this plan row represent the borrower's current plan (matching plan,
// TLF variant, and PSLF variant)?
export function isCurrentPlanRow(row: SortedPlanRow, section: Sf0726ResultSection): boolean {
  // PSLF only counts on income-driven plans, so a borrower can only be matched
  // to a PSLF row when their current plan is PSLF-eligible. For any other plan
  // the engine emits a single pslf=false row, so requiring pslf=true (when the
  // borrower is pursuing PSLF) would drop that loan from the comparison
  // entirely — mirror the backend rule here instead.
  const expectedPslf = section.currentPslf && isPslfEligiblePlanKey(section.currentPlan);
  return (
    !!section.currentPlan &&
    row.plan === section.currentPlan &&
    row.tlf_amount === section.currentTlfAmount &&
    row.pslf === expectedPslf
  );
}

// Month/Year x-axis labels for an amortization graph, one per month, starting
// from the analysis date (the recommendation's creation date). The label
// formatter is injected so the caller can localize via ember-intl. Day 1 keeps
// the month arithmetic clean across month-length boundaries and year rollover.
export function scheduleMonthLabels(
  analysisDate: Date | string | undefined,
  count: number,
  format: (date: Date) => string
): string[] {
  // Derive and build the months in UTC so a createdAt timestamp at e.g.
  // 2026-07-01T00:00:00Z isn't shifted to the previous month for viewers west of
  // UTC. The caller's formatter must likewise format in UTC.
  const base = analysisDate ? new Date(analysisDate) : new Date();
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  return Array.from({ length: count }, (_unused, i) =>
    format(new Date(Date.UTC(year, month + i, 1)))
  );
}

// Build a single-plan generate payload (for the amortization graph) from a
// stored loan input plus the selected plan, its TLF amount, and PSLF flag.
export function buildGeneratePayload(
  input: SortPlansPayload,
  plan: string,
  tlfAmount: number,
  pslf: boolean
): GeneratePaymentPlanPayload {
  return {
    interest_rate_annual: input.interest_rate_annual,
    payment_plan: plan,
    current_balance: input.current_balance,
    paid_months: input.current_paid_months,
    original_disbursement_amount: input.original_disbursement_amount,
    agi: input.agi,
    agi_growth_rate: input.agi_growth_rate,
    number_of_children: input.number_of_children,
    number_of_adults: input.number_of_adults,
    first_loan_time: input.first_loan_time,
    last_loan_time: input.last_loan_time,
    loan_type: input.loan_type,
    tlf_amount: tlfAmount,
    pslf,
    residence_state: input.residence_state,
    payment_events: [],
    multiplier: input.multiplier,
  };
}

// Full questionnaire prefill (every answer) sourced from the shared answer
// profile, so a value the user entered on any page pre-fills here, on both
// first and repeat visits.
export type Sf0726FullPrefill = {
  payoffGoal: string;
  affordPayments: string;
  agiDollars: number;
  adultsChoice: string;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  residenceState: string;
  currentlyWorkingToward: string;
  currentForgivenessProgram: string;
  madePayments: number;
  isGov: boolean;
  isTeacher: boolean;
  isOther: boolean;
  planInGov: string;
  planAsTeacher: string;
  employmentMonths: number;
  tlfQualifies17500: boolean;
};

// Map the shared answer profile onto every questionnaire field. Fields absent
// on older profiles read back as blank/zero (backward compatible). AGI reuses
// the shared household-income answer; the employment checkboxes are reconstructed
// from the stored combined employment-type string.
export function answerProfilePrefill(
  answerProfile: AnswerProfileModel | undefined | null
): Sf0726FullPrefill {
  const employmentType = answerProfile?.employmentType ?? '';
  return {
    payoffGoal: answerProfile?.payoffGoal ?? '',
    affordPayments: answerProfile?.affordPayments ?? '',
    agiDollars: answerProfile?.householdIncome ? answerProfile.householdIncome / 100 : 0,
    adultsChoice: answerProfile?.numberOfAdults ? String(answerProfile.numberOfAdults) : '',
    numberOfChildren: answerProfile?.numberOfChildren ?? 0,
    firstLoanTime: answerProfile?.federalFirstLoanTime ?? '',
    lastLoanTime: answerProfile?.federalLastLoanTime ?? '',
    residenceState: answerProfile?.residenceState ?? '',
    currentlyWorkingToward: answerProfile?.currentWorkingOnForgiveness ?? '',
    currentForgivenessProgram: answerProfile?.currentForgivenessProgram ?? '',
    madePayments: answerProfile?.madePayments ?? 0,
    isGov: employmentType.includes('government-tribal-nonprofit'),
    isTeacher: employmentType.includes('teacher'),
    isOther: employmentType === 'other-private-sector',
    planInGov: answerProfile?.planInGovernment ?? '',
    planAsTeacher: answerProfile?.planAsTeacher ?? '',
    employmentMonths: answerProfile?.madeLimitedWaiverPayments ?? 0,
    tlfQualifies17500: answerProfile?.teacherLoanForgiveness === 'tlf17500',
  };
}

// NSLDS-derived answers we can pre-fill (and the dates to explain them). All of
// this is computed server-side and stored on the wellness train when an NSLDS
// file has been uploaded — see derive_loan_timing / get_and_set_wellness_train.
// These take precedence over the answer-profile prefill (the user can still edit).
export type Sf0726NsldsPrefill = {
  nsldsAsOf: string | null; // when the NSLDS data was generated (display)
  firstLoanDate: string | null; // first federal loan origination date (display)
  lastLoanDate: string | null; // most-recent loan origination date (display)
  firstLoanTime: string | null; // first-loan cohort, or null if not derivable
  lastLoanTime: string | null; // last-loan cohort, or null if not derivable
  pslfCount: number | null; // PSLF qualifying payments, only when shown and > 0
};

// The wellness-train fields we read (raw snake_case, as the wellness endpoint
// returns them — same shape tio-admin's payment-plan-explorer reads pslf_count from).
type WellnessNsldsFields = {
  nslds?: boolean;
  nslds_as_of?: string | null;
  first_loan_date?: string | null;
  last_loan_date?: string | null;
  first_loan_time?: string | null;
  last_loan_time?: string | null;
  pslf_count?: number | null;
  pslf_count_shown?: boolean | null;
};

// Build the NSLDS prefill from the wellness-train response. Returns null when no
// NSLDS file is on record, or when nothing usable was derived from it.
export function wellnessNsldsPrefill(
  wellness: WellnessNsldsFields | null | undefined
): Sf0726NsldsPrefill | null {
  if (!wellness?.nslds) {
    return null;
  }
  const firstLoanTime = wellness.first_loan_time ?? '';
  const lastLoanTime = wellness.last_loan_time ?? '';
  const pslfShown = !!wellness.pslf_count_shown && (wellness.pslf_count ?? 0) > 0;
  const prefill: Sf0726NsldsPrefill = {
    nsldsAsOf: wellness.nslds_as_of ?? null,
    firstLoanDate: wellness.first_loan_date ?? null,
    lastLoanDate: wellness.last_loan_date ?? null,
    // Only accept values that are real cohort options (defensive against drift).
    firstLoanTime: (FIRST_LOAN_TIME_VALUES as readonly string[]).includes(firstLoanTime)
      ? firstLoanTime
      : null,
    lastLoanTime: (LAST_LOAN_TIME_VALUES as readonly string[]).includes(lastLoanTime)
      ? lastLoanTime
      : null,
    pslfCount: pslfShown ? Number(wellness.pslf_count) : null,
  };
  const hasAnything = prefill.firstLoanTime || prefill.lastLoanTime || prefill.pslfCount !== null;
  return hasAnything ? prefill : null;
}

// ---------------------------------------------------------------------------
// Per-plan descriptive copy (subtitle / best-for / pros / cons / instructions)
// shown on the results page for the best, current, and compared plans.
//
// Each field below is a TRANSLATION KEY (not prose), mirroring the array-of-keys
// convention used by app/components/app/page/pros-vs-cons. All copy lives in the
// SF-0726 namespace `strategy_finder_0726.plan_copy.*` (translations/<locale>/...),
// self-contained so tuning it never affects the legacy repayment-plans education
// page. Plans with no copy yet (refinancing) map every field to a single shared
// "text missing" marker via `missingPlanCopy`.
export type PlanCopyKeys = {
  subtitle: string;
  bestFor: string;
  pros: string[];
  cons: string[];
  instructions: string[];
};

// Shared placeholder key for copy that has not been written yet (RAP / new
// Standard). The plan-copy component detects this key and renders a visible
// "needs to be added" marker rather than a blank section.
export const PLAN_COPY_MISSING_KEY = 'strategy_finder_0726.plan_copy.text_missing';

function missingPlanCopy(): PlanCopyKeys {
  return {
    subtitle: PLAN_COPY_MISSING_KEY,
    bestFor: PLAN_COPY_MISSING_KEY,
    pros: [PLAN_COPY_MISSING_KEY],
    cons: [PLAN_COPY_MISSING_KEY],
    instructions: [PLAN_COPY_MISSING_KEY],
  };
}

// All SF-0726 per-plan copy lives under the strategy_finder_0726 namespace in
// tio-common's translation bundle (decoupled from the shared glossary so tuning
// here never affects the legacy repayment-plans page).
const sf = (path: string) => `strategy_finder_0726.plan_copy.${path}`;

// Shared enrollment steps for the income-driven plans (PAYE / SAVE / IBR / ICR / RAP).
const idrInstructions = [
  sf('income_driven.instructions.step_1'),
  sf('income_driven.instructions.step_2'),
  sf('income_driven.instructions.step_3'),
];

export const PLAN_COPY_KEYS: Record<string, PlanCopyKeys> = {
  tenYearsStandard: {
    subtitle: sf('tenYearsStandard.subtitle'),
    bestFor: sf('tenYearsStandard.best_for'),
    pros: [
      sf('tenYearsStandard.pros.pro_1'),
      sf('tenYearsStandard.pros.pro_2'),
      sf('tenYearsStandard.pros.pro_3'),
    ],
    cons: [sf('tenYearsStandard.cons.con_1'), sf('tenYearsStandard.cons.con_2')],
    instructions: [
      sf('tenYearsStandard.instructions.step_1'),
      sf('tenYearsStandard.instructions.step_2'),
    ],
  },
  graduated: {
    subtitle: sf('graduated.subtitle'),
    bestFor: sf('graduated.best_for'),
    pros: [sf('graduated.pros.pro_1'), sf('graduated.pros.pro_2'), sf('graduated.pros.pro_3')],
    cons: [
      sf('graduated.cons.con_1'),
      sf('graduated.cons.con_2'),
      sf('graduated.cons.con_3'),
      sf('graduated.cons.con_4'),
    ],
    instructions: [
      sf('graduated.instructions.step_1'),
      sf('graduated.instructions.step_2'),
      sf('graduated.instructions.step_3'),
    ],
  },
  extendedPlan: {
    subtitle: sf('extendedPlan.subtitle'),
    bestFor: sf('extendedPlan.best_for'),
    pros: [sf('extendedPlan.pros.pro_1'), sf('extendedPlan.pros.pro_2')],
    cons: [sf('extendedPlan.cons.con_1'), sf('extendedPlan.cons.con_2')],
    instructions: [sf('extendedPlan.instructions.step_1'), sf('extendedPlan.instructions.step_2')],
  },
  paye: {
    subtitle: sf('paye.subtitle'),
    bestFor: sf('paye.best_for'),
    pros: [sf('paye.pros.pro_1'), sf('paye.pros.pro_2')],
    cons: [sf('paye.cons.con_1'), sf('paye.cons.con_2')],
    instructions: idrInstructions,
  },
  repaye: {
    subtitle: sf('repaye.subtitle'),
    bestFor: sf('repaye.best_for'),
    pros: [sf('repaye.pros.pro_1'), sf('repaye.pros.pro_2')],
    cons: [sf('repaye.cons.con_1'), sf('repaye.cons.con_2')],
    instructions: idrInstructions,
  },
  ibr: {
    subtitle: sf('ibr.subtitle'),
    bestFor: sf('ibr.best_for'),
    pros: [sf('ibr.pros.pro_1'), sf('ibr.pros.pro_2'), sf('ibr.pros.pro_3')],
    cons: [sf('ibr.cons.con_1'), sf('ibr.cons.con_2')],
    instructions: idrInstructions,
  },
  icr: {
    subtitle: sf('icr.subtitle'),
    bestFor: sf('icr.best_for'),
    pros: [sf('icr.pros.pro_1'), sf('icr.pros.pro_2'), sf('icr.pros.pro_3')],
    cons: [sf('icr.cons.con_1'), sf('icr.cons.con_2'), sf('icr.cons.con_3')],
    instructions: idrInstructions,
  },
  repaymentAssistancePlan: {
    subtitle: sf('repaymentAssistancePlan.subtitle'),
    bestFor: sf('repaymentAssistancePlan.best_for'),
    pros: [sf('repaymentAssistancePlan.pros.pro_1'), sf('repaymentAssistancePlan.pros.pro_2')],
    cons: [
      sf('repaymentAssistancePlan.cons.con_1'),
      sf('repaymentAssistancePlan.cons.con_2'),
      sf('repaymentAssistancePlan.cons.con_3'),
      sf('repaymentAssistancePlan.cons.con_4'),
      sf('repaymentAssistancePlan.cons.con_5'),
    ],
    instructions: idrInstructions,
  },
  newStandardPlan: {
    subtitle: sf('newStandardPlan.subtitle'),
    bestFor: sf('newStandardPlan.best_for'),
    pros: [
      sf('newStandardPlan.pros.pro_1'),
      sf('newStandardPlan.pros.pro_2'),
      sf('newStandardPlan.pros.pro_3'),
    ],
    cons: [sf('newStandardPlan.cons.con_1'), sf('newStandardPlan.cons.con_2')],
    instructions: [
      sf('newStandardPlan.instructions.step_1'),
      sf('newStandardPlan.instructions.step_2'),
    ],
  },
  // Refinancing has no copy yet — renders the visible "needs to be added" marker.
  refinancing: missingPlanCopy(),
};

// Resolve the copy keys for a plan code, falling back to the "missing" marker
// for any code without an entry (e.g. the new RAP / Standard plans, or an
// unexpected code).
export function planCopyKeys(plan: string | null | undefined): PlanCopyKeys {
  return (plan && PLAN_COPY_KEYS[plan]) || missingPlanCopy();
}

// Public Service Loan Forgiveness overlay copy, shown in addition to the base
// plan when a row uses PSLF (pslf === true). Ported from the legacy tio-api
// strategy finder.
export const PSLF_COPY: PlanCopyKeys = {
  subtitle: sf('pslf.subtitle'),
  bestFor: sf('pslf.best_for'),
  pros: [sf('pslf.pros.pro_1'), sf('pslf.pros.pro_2')],
  cons: [sf('pslf.cons.con_1'), sf('pslf.cons.con_2')],
  instructions: [
    sf('pslf.instructions.step_1'),
    sf('pslf.instructions.step_2'),
    sf('pslf.instructions.step_3'),
  ],
};

// Teacher Loan Forgiveness overlay copy, shown in addition to the base plan
// when a row applies TLF (tlf_amount > 0). Ported from the legacy tio-api
// strategy finder.
export const TLF_COPY: PlanCopyKeys = {
  subtitle: sf('tlf.subtitle'),
  bestFor: sf('tlf.best_for'),
  pros: [sf('tlf.pros.pro_1'), sf('tlf.pros.pro_2')],
  cons: [sf('tlf.cons.con_1'), sf('tlf.cons.con_2'), sf('tlf.cons.con_3')],
  instructions: [sf('tlf.instructions.step_1'), sf('tlf.instructions.step_2')],
};

// The full set of answers written to the shared answer profile on submit, so
// other pages pre-fill from the user's latest entries here (and vice versa).
// AGI is stored as household income; household size is adults + children.
export function answerProfileUpdates(formModel: Sf0726FormModel) {
  // The teacher tier is stored on the shared `teacherLoanForgiveness` answer so
  // the $17,500-vs-$5,000 choice round-trips on a repeat visit.
  const teacherLoanForgiveness = formModel.isTeacher
    ? formModel.tlfQualifies17500
      ? 'tlf17500'
      : 'tlf5000'
    : 'none';
  return {
    payoffGoal: formModel.payoffGoal,
    affordPayments: formModel.affordPayments,
    householdIncome: formModel.agi,
    householdSize: formModel.numberOfAdults + formModel.numberOfChildren,
    residenceState: formModel.residenceState,
    numberOfAdults: formModel.numberOfAdults,
    numberOfChildren: formModel.numberOfChildren,
    federalFirstLoanTime: formModel.firstLoanTime,
    federalLastLoanTime: formModel.lastLoanTime,
    // Loan-forgiveness flow (shared with the existing Repayment Strategy Finder).
    currentWorkingOnForgiveness: formModel.currentlyWorkingToward,
    currentForgivenessProgram: formModel.currentForgivenessProgram,
    madePayments: formModel.madePayments,
    employmentType: deriveEmploymentType(formModel.isGov, formModel.isTeacher, formModel.isOther),
    planInGovernment: formModel.planInGov,
    planAsTeacher: formModel.planAsTeacher,
    madeLimitedWaiverPayments: formModel.employmentMonths,
    teacherLoanForgiveness,
  };
}
